import React, { useEffect, useState } from 'react'
import { Form, Input, Button, Typography, message, Skeleton } from 'antd'
import { platformSettingsService, type PlatformSetting } from '../../../shared/api/services/platform-settings.service'

const { Title } = Typography

export const EnterpriseBillingSettings: React.FC = () => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        fetchSettings()
    }, [])

    const fetchSettings = async () => {
        try {
            setLoading(true)
            const res = await platformSettingsService.getSettings()
            const allSettings = res.data || {}
            
            // Flatten grouped settings to find billing-related ones
            const flatSettings: Record<string, string> = {}
            Object.values(allSettings).forEach((group: PlatformSetting[]) => {
                group.forEach((s) => {
                    if (s.key === 'per_active_user_price' || s.key === 'gst_percentage') {
                        flatSettings[s.key] = String(s.value)
                    }
                })
            })

            form.setFieldsValue({
                per_active_user_price: flatSettings.per_active_user_price || '199.00',
                gst_percentage: flatSettings.gst_percentage || '18.00',
            })
        } catch (error) {
            console.error('Failed to load settings', error)
            message.error('Failed to load enterprise billing settings')
            form.setFieldsValue({
                per_active_user_price: '199.00',
                gst_percentage: '18.00',
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async (values: any) => {
        try {
            setSaving(true)
            
            // platformSettingsService expects { key: value } map
            await platformSettingsService.updateSettings({
                per_active_user_price: values.per_active_user_price,
                gst_percentage: values.gst_percentage,
            })

            message.success('Enterprise billing settings saved successfully!')
        } catch (error) {
            console.error('Failed to save settings', error)
            message.error('Failed to save settings')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return <Skeleton active paragraph={{ rows: 4 }} />
    }

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
        >
            <Title level={4}>Enterprise Staff Billing</Title>
            <Form.Item 
                label="Base Price per Active Staff (Monthly)" 
                name="per_active_user_price" 
                rules={[{ required: true, message: 'Please set the base price' }]}
                tooltip="This price is charged monthly per active staff member (Owner is free)."
            >
                <Input prefix="₹" type="number" step="0.01" />
            </Form.Item>
            
            <Form.Item 
                label="GST Percentage" 
                name="gst_percentage" 
                rules={[{ required: true, message: 'Please set the GST percentage' }]}
            >
                <Input suffix="%" type="number" step="0.01" />
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={saving}>
                    Save Pricing
                </Button>
            </Form.Item>
        </Form>
    )
}
